// src/Routes/control.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { withLoading } from '@app/hocs/withLoading.hoc';
import type { RouteConfig } from '@app/components/header/components/Breadcrumb/DynamicBreadcrumb';
import { getAuthAndLogout, protectedLayout, sharedRootChildren } from './shared';
import InvalidPermission from '@app/pages/ErrorInvalidPermission'; // Adjust the path to your `InvalidPermission` component

// lazy imports
const UserList = withLoading(React.lazy(() => import('@app/panels/control/pages/AdminUsers/AdminUserListPage')));
const UserAdd = withLoading(React.lazy(() => import('@app/panels/control/pages/AdminUsers/AdminUserAddPage')));
const UserEdit = withLoading(React.lazy(() => import('@app/panels/control/pages/AdminUsers/AdminUserEditPage')));
const ChangePassword = withLoading(React.lazy(() => import('@app/pages/ChangePasswords/ChangePasswordPages')));

const EndUserList = withLoading(React.lazy(() => import('@app/panels/control/pages/endUsers/EndUserListPage')));
const EndUserAdd = withLoading(React.lazy(() => import('@app/panels/control/pages/endUsers/EndUserAddPage')));
const EndUserEdit = withLoading(React.lazy(() => import('@app/panels/control/pages/endUsers/EndUserEditPage')));


const UserRolesList = withLoading(React.lazy(() => import('@app/pages/UserRoles/UserRolesListPage')));
const UserRolesAdd = withLoading(React.lazy(() => import('@app/pages/UserRoles/UserRolesAddPage')));
const UserRolesEdit = withLoading(React.lazy(() => import('@app/pages/UserRoles/UserRolesEditPage')));

const ReportingManageInfoList = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingManageInfoListPages')));
const ReportingManageInfo = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingManageInfoPages')));
const ReportingCategoryAdd = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingCategoryAddPages')));
const ReportingDetailsList = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingDetailsListPages')));
const ReportingDetailsAdd = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingDetailsAddPages')));
const ReportingCategoryEdit = withLoading(React.lazy(() => import('@app/pages/Reportings/ReportingCategoryEditPages')));


const SealTypesListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealTypes/SealTypesListPage')));
const SealTypesAddPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealTypes/SealTypesAddPage')));
const SealTypesEditPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealTypes/SealTypesEditPage')));


const SealGroupsListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealGroups/SealGroupsListPage')));
const SealGroupMapPage = withLoading(React.lazy(() => import('@app/panels/control/pages/sealGroups/SealGroupMapPage')));

const LuggageBuckleTypesListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/luggageBuckleTypes/LuggageBuckleTypesListPage')));
const LuggageBuckleTypesAddPage = withLoading(React.lazy(() => import('@app/panels/control/pages/luggageBuckleTypes/LuggageBuckleTypesAddPage')));
const LuggageBuckleTypesEditPage = withLoading(React.lazy(() => import('@app/panels/control/pages/luggageBuckleTypes/LuggageBuckleTypesEditPage')));

const FactoryPrintingListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/factoryPrintings/FactoryPrintingListPage')));
const GenerateFactoryBatchListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/factoryPrintings/GenerateFactoryBatchListPage')));
const FactoryScanningListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/factoryScannings/FactoryScanningListPage')));
const AdminApprovalListPage = withLoading(React.lazy(() => import('@app/panels/control/pages/adminApprovals/adminApprovalListPage')));

const DistributorList = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-distributor/DistributorList')));
const DistributorAdd = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-distributor/DistributorAdd')));
const DistributorEdit = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-distributor/DistributorEdit')));
const DistributorApprove = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-distributor/DistributorApprove')));
const DistributorView = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-distributor/DistributorView')));

const OnlineMarketerList = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-online-marketer/OnlineMarketerList')));
const OnlineMarketerAdd = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-online-marketer/OnlineMarketerAdd')));
const OnlineMarketerEdit = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-online-marketer/OnlineMarketerEdit')));
const OnlineMarketerApprove = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-online-marketer/OnlineMarketerApprove')));
const OnlineMarketerView = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-online-marketer/OnlineMarketerView')));

const SalesAgentList = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-sales-agent/SalesAgentList')));
const SalesAgentAdd = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-sales-agent/SalesAgentAdd')));
const SalesAgentEdit = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-sales-agent/SalesAgentEdit')));
const SalesAgentApprove = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-sales-agent/SalesAgentApprove')));
const SalesAgentView = withLoading(React.lazy(() => import('@app/components/eMIPs/manage-sales-agent/SalesAgentView')));

const CommissionPayouts = withLoading(React.lazy(() => import('@app/pages/CommissionPayouts/CommissionPayoutsPage')));
const CommissionPayoutsAdd = withLoading(React.lazy(() => import('@app/pages/CommissionPayouts/CommissionPayoutsAddPage')));
const CommissionPayoutsEdit = withLoading(React.lazy(() => import('@app/pages/CommissionPayouts/CommissionPayoutsEditPage')));
const CommissionPayoutsApprove = withLoading(React.lazy(() => import('@app/pages/CommissionPayouts/CommissionPayoutsApprovePage')));
const CommissionPayoutsUploadSlip = withLoading(React.lazy(() => import('@app/pages/CommissionPayouts/CommissionPayoutsCompletePage')));
const CommissionPayoutsView = withLoading(React.lazy(() => import('@app/pages/CommissionPayouts/CommissionPayoutsViewPage')));

const CommissionPlans = withLoading(React.lazy(() => import('@app/pages/CommissionPlans/CommissionPlansPage')));
const CommissionPlansAdd = withLoading(React.lazy(() => import('@app/pages/CommissionPlans/CommissionPlansAddPage')));
const CommissionPlansEdit = withLoading(React.lazy(() => import('@app/pages/CommissionPlans/CommissionPlansEditPage')));
export const controlRoutes: RouteConfig[] = [
  {

    path: '/',
    element: protectedLayout,
    breadcrumb: 'Dashboard',
    children: [
      ...sharedRootChildren,

      // // users
      {
        path: 'users',
        breadcrumb: 'Users',
        children: [
          { path: "add", element: <UserAdd />, breadcrumb: "Add User" },
          { path: 'edit/:id', element: <UserEdit />, breadcrumb: 'Edit User' },
          { index: true, element: <UserList />, breadcrumb: 'Users' },
          { path: 'ChangePassword', element: <ChangePassword />, breadcrumb: 'ChangePassword' },
        ],
      },
      {
        path: 'endUsers',
        breadcrumb: 'End Users',
        children: [
          { path: "add", element: <EndUserAdd />, breadcrumb: "Add User" },
          { path: 'edit/:id', element: <EndUserEdit />, breadcrumb: 'Edit User' },
          { index: true, element: <EndUserList />, breadcrumb: 'Users' },
          { path: 'ChangePassword', element: <ChangePassword />, breadcrumb: 'ChangePassword' },
        ],
      },
      // user-roles
      {
        path: 'user-roles',
        breadcrumb: 'User Roles',
        children: [
          { path: 'add', element: <UserRolesAdd />, breadcrumb: 'Add User Role' },
          { path: 'edit/:id', element: <UserRolesEdit />, breadcrumb: 'Edit User Role' },
          { index: true, element: <UserRolesList />, breadcrumb: 'User Roles' },
        ],
      },
      {
        path: "sealTypes",
        breadcrumb: "Seal Types",
        children: [
          { path: "list", element: <SealTypesListPage />, breadcrumb: "List" },
          { path: "sealTypes-add", element: <SealTypesAddPage />, breadcrumb: "Add Seal Type" },
          { path: "sealTypes-edit/:id", element: <SealTypesEditPage />, breadcrumb: "Edit Seal Type" },
        ],
      },
      {
        path: "sealGroups",
        breadcrumb: "Seal Groups",
        children: [
          { path: "list", element: <SealGroupsListPage />, breadcrumb: "List" },
          { path: "sealGroups-map/:id", element: <SealGroupMapPage />, breadcrumb: "Map Seals" },
          // { path: "sealTypes-add", element: <SealTypesAddPage />, breadcrumb: "Add Seal Type" },
          // { path: "sealTypes-edit/:id", element: <SealTypesEditPage />, breadcrumb: "Edit Seal Type" },
        ],
      },
      {
        path: "luggageBuckleTypes",
        breadcrumb: "Luggage Buckle Types",
        children: [
          { path: "list", element: <LuggageBuckleTypesListPage />, breadcrumb: "List" },
          { path: "luggageBuckleTypes-add", element: <LuggageBuckleTypesAddPage />, breadcrumb: "Add Luggage Buckle Type" },
          { path: "luggageBuckleTypes-edit/:id", element: <LuggageBuckleTypesEditPage />, breadcrumb: "Edit Luggage Buckle Type" },
        ],
      },
      {
        path: "factoryPrinting",
        breadcrumb: "Factory Printing",
        children: [
          { path: "printing", element: <FactoryPrintingListPage />, breadcrumb: "printing" },
          { path: "generate", element: <GenerateFactoryBatchListPage />, breadcrumb: "generatePrinting" },
        ],
      },
      {
        path: "factoryScanning",
        breadcrumb: "Factory Scanning",
        children: [
          { path: "list", element: <FactoryScanningListPage />, breadcrumb: "scanning" },
        ],
      },
          {
        path: "adminApproval",
        breadcrumb: "Admin Approval",
        children: [
          { path: "list", element: <AdminApprovalListPage />, breadcrumb: "adminApproval" },
        ],
      },
      // reporting
      {
        path: 'reporting',
        breadcrumb: 'Reporting',
        children: [
          { path: 'reporting/list', element: <ReportingManageInfoList />, breadcrumb: 'reportList' },
          { path: 'reporting-manage/:id', element: <ReportingManageInfo />, breadcrumb: 'Manage Report' },
          { path: 'reporting-category-add', element: <ReportingCategoryAdd />, breadcrumb: 'Add Reporting Category' },
          { path: 'reporting-category-list/:id', element: <ReportingDetailsList />, breadcrumb: 'Reporting Category List' },
          { path: 'reporting-details-add', element: <ReportingDetailsAdd />, breadcrumb: 'Add Reporting Details' },
          { path: 'reporting-category-edit/:id', element: <ReportingCategoryEdit />, breadcrumb: 'Edit Reporting Category' },
        ],
      },
      {
        path: 'online-marketers',
        breadcrumb: 'Online Marketers',
        children: [
          { path: 'add', element: <OnlineMarketerAdd />, breadcrumb: 'Add Online Marketer' },
          { path: 'approve/:id', element: <OnlineMarketerApprove />, breadcrumb: 'Approve Online Marketer' },
          { path: 'view/:id', element: <OnlineMarketerView />, breadcrumb: 'View Online Marketer' },
          { path: 'edit/:id', element: <OnlineMarketerEdit />, breadcrumb: 'Edit Online Marketer' },
          { path: '', element: <OnlineMarketerList />, breadcrumb: 'Online Marketers' },
        ],
      },
      {
        path: 'sales-agents',
        breadcrumb: 'Sales Agents',
        children: [
          { path: 'add', element: <SalesAgentAdd />, breadcrumb: 'Add Sales Agent' },
          { path: 'approve/:id', element: <SalesAgentApprove />, breadcrumb: 'Approve Sales Agent' },
          { path: 'view/:id', element: <SalesAgentView />, breadcrumb: 'View Sales Agent' },
          { path: 'edit/:id', element: <SalesAgentEdit />, breadcrumb: 'Edit Sales Agent' },
          { path: '', element: <SalesAgentList />, breadcrumb: 'Sales Agents' },
        ],
      },
      {
        path: 'distributors',
        breadcrumb: 'Distributors',
        children: [
          { path: 'add', element: <DistributorAdd />, breadcrumb: 'Add Distributor' },
          { path: 'approve/:id', element: <DistributorApprove />, breadcrumb: 'Approve Distributor' },
          { path: 'view/:id', element: <DistributorView />, breadcrumb: 'View Distributor' },
          { path: 'edit/:id', element: <DistributorEdit />, breadcrumb: 'Edit Distributor' },
          { path: '', element: <DistributorList />, breadcrumb: 'Distributors' },
        ],
      },
      {
        path: 'commission-plans',
        breadcrumb: 'Manage Commission Plans',
        children: [
          { path: 'add', element: <CommissionPlansAdd />, breadcrumb: 'Add Commission Plan' },
          { path: 'edit/:id', element: <CommissionPlansEdit />, breadcrumb: 'Edit Commission Plan' },
          { path: '', element: <CommissionPlans />, breadcrumb: 'Manage Commission Plans' },
        ],
      },
      {
        path: 'commission-payouts',
        breadcrumb: 'Manage Commission Payouts',
        children: [
          { path: 'add', element: <CommissionPayoutsAdd />, breadcrumb: 'Add Commission Payout' },
          { path: 'edit/:id', element: <CommissionPayoutsEdit />, breadcrumb: 'Edit Commission Payout' },
          { path: 'approve/:id', element: <CommissionPayoutsApprove />, breadcrumb: 'Approve Commission Payout' },
          { path: 'upload-slip/:id', element: <CommissionPayoutsUploadSlip />, breadcrumb: 'Upload Slip' },
          { path: 'view/:id', element: <CommissionPayoutsView />, breadcrumb: 'View Commission Payout' },
          { path: '', element: <CommissionPayouts />, breadcrumb: 'Manage Commission Payouts' },
        ],
      },
      { path: '*', element: <InvalidPermission />, breadcrumb: 'Invalid Permission' },
    ],
  },

  // Auth + Logout (root-level, **not** nested under protectedLayout)
  ...getAuthAndLogout('control'),

  // Catch-all → redirect to login

];

export default controlRoutes;
